import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, AlertCircle } from "lucide-react";
import { autoCorrelate } from "@/lib/pitch";
import { freqToNote } from "@/lib/notes";

export default function Tuner() {
    const [listening, setListening] = useState(false);
    const [error, setError] = useState(null);
    const [freq, setFreq] = useState(0);
    const [smoothFreq, setSmoothFreq] = useState(0);

    const streamRef = useRef(null);
    const ctxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const bufRef = useRef(null);

    const stop = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (ctxRef.current) {
            ctxRef.current.close().catch(() => {});
            ctxRef.current = null;
        }
        analyserRef.current = null;
        bufRef.current = null;
        setListening(false);
        setFreq(0);
        setSmoothFreq(0);
    };

    const start = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    autoGainControl: false,
                    noiseSuppression: false,
                },
            });
            streamRef.current = stream;
            const AC = window.AudioContext || window.webkitAudioContext;
            const ctx = new AC();
            ctxRef.current = ctx;
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 2048;
            analyserRef.current = analyser;
            bufRef.current = new Float32Array(analyser.fftSize);
            src.connect(analyser);
            setListening(true);

            let smoothed = 0;
            const loop = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getFloatTimeDomainData(bufRef.current);
                const f = autoCorrelate(bufRef.current, ctx.sampleRate);
                if (f > 0) {
                    smoothed = smoothed ? smoothed * 0.6 + f * 0.4 : f;
                    setFreq(f);
                    setSmoothFreq(smoothed);
                } else {
                    smoothed = smoothed * 0.92;
                    setFreq(0);
                    setSmoothFreq((p) => p * 0.92);
                }
                rafRef.current = requestAnimationFrame(loop);
            };
            loop();
        } catch (e) {
            console.error("Tuner mic error:", e);
            const friendly =
                e?.name === "NotAllowedError"
                    ? "Microphone access denied. Please allow microphone permission in your browser settings to use the tuner."
                    : e?.name === "NotFoundError"
                      ? "No microphone detected. Please connect a microphone and try again."
                      : "Could not start the tuner. Make sure your browser supports microphone access (Chrome, Edge, Safari) and that the page is served over HTTPS.";
            setError(friendly);
            setListening(false);
        }
    };

    useEffect(() => () => stop(), []);

    const note = freqToNote(smoothFreq || 0);
    const cents = smoothFreq > 0 ? note.cents : 0;
    const inTune = smoothFreq > 0 && Math.abs(cents) <= 5;

    // Gauge: -50 to +50 cents → -90deg to +90deg
    const angle = Math.max(-50, Math.min(50, cents)) * 1.8;

    return (
        <div data-testid="tuner-page" className="space-y-10">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Tool · 04
                </div>
                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
                    Flute Tuner
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    Real-time microphone pitch detection with cents-accurate
                    tuning. Built for bansuri, voice and any wind instrument.
                </p>
            </header>

            {error && (
                <div
                    data-testid="tuner-error"
                    className="flex items-start gap-3 p-4 rounded-md border border-destructive/40 bg-destructive/10 text-destructive"
                >
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <div className="text-sm">{error}</div>
                </div>
            )}

            <div className="grid lg:grid-cols-5 gap-6">
                {/* Gauge */}
                <div className="lg:col-span-3 rounded-lg border border-border bg-card p-8 relative overflow-hidden">
                    <div className="absolute inset-0 schematic opacity-50 pointer-events-none" />
                    <div className="relative">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-6">
                            Tuning meter · cents
                        </div>
                        {/* Gauge SVG */}
                        <div className="grid place-items-center">
                            <svg
                                viewBox="0 0 400 220"
                                className="w-full max-w-md"
                                data-testid="tuner-gauge"
                            >
                                {/* Arc background */}
                                <path
                                    d="M 40 200 A 160 160 0 0 1 360 200"
                                    fill="none"
                                    stroke="hsl(var(--border))"
                                    strokeWidth="2"
                                />
                                {/* Tick marks */}
                                {Array.from({ length: 11 }).map((_, i) => {
                                    const a = -90 + i * 18;
                                    const rad = (a * Math.PI) / 180;
                                    const x1 = 200 + Math.sin(rad) * 145;
                                    const y1 = 200 - Math.cos(rad) * 145;
                                    const x2 = 200 + Math.sin(rad) * 160;
                                    const y2 = 200 - Math.cos(rad) * 160;
                                    const isCenter = i === 5;
                                    return (
                                        <line
                                            key={i}
                                            x1={x1}
                                            y1={y1}
                                            x2={x2}
                                            y2={y2}
                                            stroke={
                                                isCenter
                                                    ? "hsl(var(--accent))"
                                                    : "hsl(var(--muted-foreground))"
                                            }
                                            strokeWidth={isCenter ? 3 : 1}
                                            opacity={isCenter ? 1 : 0.5}
                                        />
                                    );
                                })}
                                {/* In-tune zone */}
                                <path
                                    d="M 191 41 A 160 160 0 0 1 209 41"
                                    fill="none"
                                    stroke="hsl(var(--accent))"
                                    strokeWidth="6"
                                    opacity="0.4"
                                />
                                {/* Needle */}
                                <g
                                    style={{
                                        transform: `rotate(${angle}deg)`,
                                        transformOrigin: "200px 200px",
                                        transition: "transform 80ms linear",
                                    }}
                                >
                                    <line
                                        x1="200"
                                        y1="200"
                                        x2="200"
                                        y2="55"
                                        stroke={
                                            inTune
                                                ? "hsl(var(--accent))"
                                                : "hsl(var(--primary))"
                                        }
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                    />
                                    <circle
                                        cx="200"
                                        cy="200"
                                        r="10"
                                        fill={
                                            inTune
                                                ? "hsl(var(--accent))"
                                                : "hsl(var(--primary))"
                                        }
                                    />
                                </g>
                                <text
                                    x="40"
                                    y="218"
                                    fill="hsl(var(--muted-foreground))"
                                    fontFamily="JetBrains Mono"
                                    fontSize="11"
                                >
                                    −50
                                </text>
                                <text
                                    x="195"
                                    y="218"
                                    fill="hsl(var(--accent))"
                                    fontFamily="JetBrains Mono"
                                    fontSize="11"
                                >
                                    0
                                </text>
                                <text
                                    x="346"
                                    y="218"
                                    fill="hsl(var(--muted-foreground))"
                                    fontFamily="JetBrains Mono"
                                    fontSize="11"
                                >
                                    +50
                                </text>
                            </svg>
                        </div>

                        {/* Detected note */}
                        <div className="mt-4 grid grid-cols-3 gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                                    Note
                                </div>
                                <div
                                    data-testid="tuner-note"
                                    className={`font-bold text-6xl ${
                                        inTune ? "text-accent" : ""
                                    }`}
                                >
                                    {smoothFreq > 0 ? note.note : "—"}
                                    {smoothFreq > 0 && (
                                        <span className="font-mono text-2xl text-muted-foreground ml-1">
                                            {note.octave}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                                    Frequency
                                </div>
                                <div
                                    data-testid="tuner-frequency"
                                    className="font-mono font-bold text-3xl tracking-tighter"
                                >
                                    {smoothFreq > 0 ? smoothFreq.toFixed(1) : "0.0"}
                                    <span className="text-sm text-muted-foreground ml-1">
                                        Hz
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                                    Cents
                                </div>
                                <div
                                    data-testid="tuner-cents"
                                    className={`font-mono font-bold text-3xl tracking-tighter ${
                                        inTune
                                            ? "text-accent"
                                            : smoothFreq > 0
                                              ? "text-primary"
                                              : ""
                                    }`}
                                >
                                    {smoothFreq > 0
                                        ? `${cents > 0 ? "+" : ""}${cents}`
                                        : "0"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls + info */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="rounded-lg border border-border bg-card p-8">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                            Microphone
                        </div>
                        <div className="font-bold text-2xl mb-6">
                            {listening ? "Listening…" : "Ready to listen"}
                        </div>
                        <button
                            data-testid="tuner-toggle-button"
                            onClick={listening ? stop : start}
                            className={`w-full h-14 rounded-md font-medium text-base flex items-center justify-center gap-2 transition-all ${
                                listening
                                    ? "bg-foreground text-background"
                                    : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                            }`}
                        >
                            {listening ? (
                                <>
                                    <MicOff size={18} /> Stop listening
                                </>
                            ) : (
                                <>
                                    <Mic size={18} /> Start tuner
                                </>
                            )}
                        </button>
                        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                            Sustain a steady note close to your microphone. The
                            needle is centered when you are within ±5 cents of
                            the target pitch.
                        </p>
                    </div>

                    {smoothFreq > 0 && (
                        <div
                            data-testid="tuner-reference"
                            className="rounded-lg border border-border bg-card p-6"
                        >
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
                                Reference target
                            </div>
                            <div className="flex items-baseline gap-3">
                                <span className="font-bold text-3xl">
                                    {note.note}
                                    {note.octave}
                                </span>
                                <span className="font-mono text-muted-foreground">
                                    {note.refFreq.toFixed(2)} Hz
                                </span>
                            </div>
                            <div className="mt-3 text-sm text-muted-foreground">
                                {inTune ? (
                                    <span className="text-accent font-medium">
                                        In tune.
                                    </span>
                                ) : cents > 0 ? (
                                    "Sharp — relax breath / push pitch down."
                                ) : (
                                    "Flat — increase breath / push pitch up."
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
